import QUERY_KEYS from '../../../query/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { getCurrentProject, getAssociatedUsers } from '../../../modules/project';
import { getTestCaseData } from '../../../modules/config';
import {
  generateStoriesFromDocument,
  generateAITestCasesFromStories,
  getIssueTypes
} from '../../../modules/jira';
import {
  UploadScreen,
  ReviewStoriesScreen,
  GenerateTCsScreen,
  ReviewTCsScreen,
} from './DocumentToJiraScreens';
import {
  SCREENS,
  WIZARD_STEPS,
  STEPPER_INDEX,
  MODAL_TITLE,
  buildCombinedDescription,
  splitCombinedDescription,
} from './wizardconfig';
import WizardGeneratingOverlay from '../../wizards/WizardGeneratingOverlay';
import WizardSuccessScreen from '../../wizards/WizardSuccessScreen';
import WizardShell from '../../wizards/WizardShell';
import CreateJiraIssueForm from '../../../components/elements/common/traceability/CreateIssueForm';
import { createDocumentStories } from './createDocumentStories';
import { defaultFormData, SAVE_PHASE } from '../../../components/project-dashboard/test-cycles/static-data';
import { useBulkSave } from '../../../components/project-dashboard/test-cases/ai/Usebulksave';
import Modal from '../../../components/elements/Modal';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import CreateTestCaseForm from '../../../components/project-dashboard/test-cases/CreateTestCaseForm';
import InMemoryStepsModal from '../../../components/project-dashboard/test-cases/ai/InMemoryStepsModal';
import AiFeedbackModal from '../../wizards/AiFeedbackModal';
import { Button } from '@mui/material';
import { useUser } from '../../../context/UserContext';

const mapApiTcToInternal = (tc, idx, testsuites, storyMeta = null) => ({
  ...defaultFormData,
  _id: idx,
  selected: false,
  summary: tc.summary || '',
  objective: tc.objective || '',
  preCondition: tc.pre_condition || '',
  module: tc.module || null,
  labels: tc.labels || [],
  technologyLayer: tc.technology_layer || '',
  coverageTag: tc.coverage_tag || 'Happy Path',
  testsuites,
  status: tc.status || defaultFormData.status,
  priority: tc.priority || defaultFormData.priority,
  owner: tc.owner || defaultFormData.owner,
  reviewer: tc.reviewer || null,
  testSteps: (tc.test_steps || []).map((s) => ({
    step: s.step || '',
    testData: s.test_data || '',
    expectedResult: s.expected_result || '',
    uri: '',
  })),
  stepsExpanded: false,
  _crossMatchKey: tc.cross_match_key ?? null,
  _crossMatchScore: tc.cross_match_score ?? null,
  _crossMatchStrength: tc.cross_match_strength ?? null,
  _automationCandidate: tc.automation_candidate ?? null,
  isAutomationNeeded: tc.automation_candidate === 'strong' ? true : defaultFormData.isAutomationNeeded,
  automationStatus: tc.automation_candidate === 'strong' ? 'To Be Planned' : (tc.automationStatus || defaultFormData.automationStatus),
  storyId: storyMeta?.id ?? null,
  storyKey: storyMeta?.key ?? null,
  storyTitle: storyMeta?.title ?? null,
});


const DocumentToJiraWizard = ({
  open,
  onClose,
  projectId,
  statuses: propStatuses,
  priorities: propPriorities,
  projectUsers: propProjectUsers,
  refetchData,
  testSuites: propTestSuites = [],
}) => {
  const [screen, setScreen] = useState(SCREENS.UPLOAD);
  const [inputTab, setInputTab] = useState('url');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [confluenceUrl, setConfluenceUrl] = useState('');
  const [stories, setStories] = useState([]);
  const [tokenUsageStories, setTokenUsageStories] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [createdJiraStories, setCreatedJiraStories] = useState([]);
  const [editingStory, setEditingStory] = useState(null);
  const [docSummary, setDocSummary] = useState(null);

  const [tcQuantity, setTcQuantity] = useState('multiple');
  const [selectedTestSuites, setSelectedTestSuites] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTcIds, setFeedbackTcIds] = useState([]);
  const [modalHidden, setModalHidden] = useState(false);
  const [reviewTCs, setReviewTCs] = useState([]);
  const [tokenUsageTCs, setTokenUsageTCs] = useState(null);
  const [tcGenerateError, setTcGenerateError] = useState(null);
  const [existingMatches, setExistingMatches] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState(new Set());
  const [editingTc, setEditingTc] = useState(null);
  const originalTcRef = useRef(null);
  const [showTcDiscardConfirm, setShowTcDiscardConfirm] = useState(false);
  const tcEditClosingRef = useRef(false);

  const setEditingTcGuarded = useCallback((valueOrUpdater) => {
    if (tcEditClosingRef.current) return;
    setEditingTc(valueOrUpdater);
  }, []);

  const canGenerateTCsRef = useRef(false);

  const [storyGroups, setStoryGroups] = useState([]);

  const [createStep, setCreateStep] = useState(1);
  const validateAllAndSubmitRef = useRef(null);
  const [showStepsModal, setShowStepsModal] = useState(false);

  const uploadedFileRef = useRef(null);
  const confluenceLinkRef = useRef(null);

  const isValidConfluenceUrl = /^https?:\/\/.+/i.test(confluenceUrl);
  const canGenerateStories =
    (inputTab === 'file' && !!uploadedFile) ||
    (inputTab === 'url' && isValidConfluenceUrl);
  const navigate = useNavigate();
  const goTo = useCallback((s) => setScreen(s), []);

  const aiGeneratedKeysRef = useRef([]);

  const navigateToTestCases = useCallback(() => {
    onClose();
    const keys = aiGeneratedKeysRef.current;
    navigate(`/project/${projectId}/testcases`, {
      state: keys.length > 0 ? { aiGeneratedKeys: keys } : undefined,
    });
  }, [onClose, navigate, projectId]);

  // Called after user submits or skips the feedback modal.
  const handleFeedbackClose = useCallback(() => {
    setFeedbackOpen(false);
    setModalHidden(false);
    navigateToTestCases();   // calls onClose() + navigate with highlighted keys
  }, [navigateToTestCases]);
  const { data: currentProject } = useQuery({
    queryKey: QUERY_KEYS.project.current(projectId),
    queryFn: () => getCurrentProject(projectId),
  });
  const linkedJiraProjects = currentProject?.linked_jira_projects || [];

  const { data: testCaseData } = useQuery({
    queryKey: ['testCaseData', projectId],
    queryFn: () => getTestCaseData(projectId),
    enabled: !!projectId,
  });

  const { data: usersResponse } = useQuery({
    queryKey: ['projectUsers', projectId],
    queryFn: () => getAssociatedUsers(projectId),
    enabled: !!projectId,
  });

  const testSuites = testCaseData?.test_suites || propTestSuites || [];
  const statuses = testCaseData?.result?.filter((i) => i.group === 'TEST_CASE_STATUS') || propStatuses || [];
  const priorities = testCaseData?.result?.filter((i) => i.group === 'TEST_CASE_PRIORITY') || propPriorities || [];
  const modules = testCaseData?.result?.filter((i) => i.group === 'FUNCTIONAL_MODULE_FIELD') || [];
  const labels = testCaseData?.result?.filter((i) => i.group === 'LABEL') || [];
  const technologyLayers = testCaseData?.result?.filter((i) => i.group === 'TECHNOLOGY_LAYER') || [];
  const projectUsers = usersResponse?.map((u) => ({ ...u.user, role: u.role })) || propProjectUsers || [];

  const { currentUser } = useUser();
  const currentUserId = currentUser?.id ?? null;
  const hasReporterWarning = !currentUser?.jira_account_id;

  const {
    savePhase,
    setSavePhase,
    saveProgress,
    saveResult,
    setSaveResult,
    handleBulkSave,
  } = useBulkSave({
    jiraReviewTCs: reviewTCs,
    setJiraReviewTCs: setReviewTCs,
    selectedStory: null,
    storyGroups,
    statuses,
    priorities,
    currentUserId,
    projectUsers,
    projectId,
    refetchData: (aiGeneratedKeys) => {
      if (aiGeneratedKeys?.length > 0) aiGeneratedKeysRef.current = aiGeneratedKeys;
      refetchData?.();
    },
    handleClose: navigateToTestCases,
    selectedExisting,
    existingMatches,
    uploadedFileRef,
    confluenceLinkRef,
    onSaveSuccess: ({ savedIds, savedKeys }) => {
      if (savedKeys?.length > 0) aiGeneratedKeysRef.current = savedKeys;
      setModalHidden(true);
      setFeedbackTcIds(savedIds);
      setFeedbackOpen(true);
    },
  });

  const selectedTCs = reviewTCs.filter((t) => t.selected);
  const hasUnresolvedWarnings = selectedTCs.some((tc) => !tc.technologyLayer);

  const handleExistingToggle = useCallback((id) => {
    setSelectedExisting((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleEditStory = useCallback((story) => {
    const selectedProject = linkedJiraProjects.find(
      (p) => p.linked_project_id === story.jiraProjectId,
    ) ?? linkedJiraProjects[0] ?? null;

    setEditingStory({
      storyId: story.id,
      summary: story.title,
      description: buildCombinedDescription(story.description, story.acceptanceCriteria),
      attachments: uploadedFile ? [uploadedFile] : [],
      labels: story.labels ?? [],
      issueType: story.issueType ?? '',
      priority: story.priority ?? '',
      reporter: story.reporter ?? null,
      assignee: story.assignee ?? null,
      selectedProject,
    });
  }, [uploadedFile, linkedJiraProjects]);

  const handleGenerateStories = useCallback(async () => {
    setGenerateError(null);
    goTo(SCREENS.GENERATING_STORIES);
    try {
      const res = await generateStoriesFromDocument({
        projectId, file: uploadedFile, confluenceUrl, inputType: inputTab,
      });
      const mapped = (res?.stories ?? []).map((s, i) => ({
        id: s.id ?? `s${i}`,
        selected: true,
        type: s.type ?? 'Story',
        title: s.summary ?? s.title ?? '',
        description: s.description ?? '',
        epic: s.epic ?? '',
        priority: s.priority ?? '',
        issueType: s.type ?? 'Story',
        labels: [],
        suggestedLabels: s.labels ?? [],
        acceptanceCriteria: s.acceptance_criteria ?? [],
        similarExists: s.similar_exists ?? false,
        storyPoints: s.story_points ?? null,
        jiraProjectId: null,
        assignee: null,
        reporter: currentUser?.jira_account_id ?? null,
      }));
      setStories(mapped);
      setDocSummary(res?.tc_context ?? null);
      setTokenUsageStories({
        total: res?.token_usage?.total_tokens ?? 0,
        input: res?.token_usage?.prompt_tokens ?? 0,
        output: res?.token_usage?.output_tokens ?? 0,
      });
      goTo(SCREENS.REVIEW_STORIES);
    } catch (err) {
      setGenerateError(err?.message ?? 'Failed to generate stories. Please try again.');
      goTo(SCREENS.UPLOAD);
    }
  }, [goTo, projectId, uploadedFile, confluenceUrl, inputTab]);

  const handleCreateInJira = useCallback(async () => {
    const selectedStories = stories.filter((s) => s.selected);
    if (selectedStories.length === 0) return;
    goTo(SCREENS.CREATING_JIRA);
    try {
      const storiesNeedingResolution = selectedStories.filter(
        (s) => s.jiraProjectId && !s.issueTypeId,
      );

      if (storiesNeedingResolution.length > 0) {
        const uniqueProjectIds = [...new Set(storiesNeedingResolution.map((s) => s.jiraProjectId))];
        const issueTypesByProject = {};
        await Promise.all(
          uniqueProjectIds.map(async (jiraProjectId) => {
            try {
              const types = await getIssueTypes(jiraProjectId, projectId);
              issueTypesByProject[jiraProjectId] = types || [];
            } catch {
              issueTypesByProject[jiraProjectId] = [];
            }
          }),
        );
        selectedStories.forEach((s, i) => {
          if (s.issueTypeId || !s.jiraProjectId) return;
          const types = issueTypesByProject[s.jiraProjectId] ?? [];
          const matched = types.find((t) => t.name === (s.issueType || 'Story'));
          if (matched) selectedStories[i] = { ...s, issueTypeId: matched.id };
        });
      }

      const createdStories = await createDocumentStories({
        stories: selectedStories,
        uploadedFile,
        inputTab,
        confluenceUrl,
        projectId,
      });
      setCreatedJiraStories(createdStories);
      goTo(SCREENS.JIRA_SUCCESS);
    } catch (error) {
      setGenerateError(error?.message || 'Failed to create Jira stories.');
      goTo(SCREENS.REVIEW_STORIES);
    }
  }, [stories, uploadedFile, inputTab, confluenceUrl, projectId, goTo]);

  const handleJiraChoice = useCallback((choice) => {
    if (choice === 'exit') { onClose(); return; }
    goTo(SCREENS.GENERATE_TCS);
  }, [onClose, goTo]);

  const handleSaveEdits = useCallback((storyId, updatedData) => {
    setStories((prev) =>
      prev.map((s) => {
        if (s.id !== storyId) return s;
        const { description, acceptanceCriteria } = splitCombinedDescription(
          updatedData.description ?? '',
          s.acceptanceCriteria,
        );
        return {
          ...s,
          title: updatedData.summary ?? s.title,
          description,
          acceptanceCriteria,
          labels: updatedData.labels ?? s.labels,
          priority: updatedData.priorityName ?? updatedData.priority ?? s.priority,
          priorityId: updatedData.priority ?? s.priorityId,
          issueType: updatedData.issueTypeName ?? updatedData.issueType ?? s.issueType,
          issueTypeId: updatedData.issueType ?? s.issueTypeId,
          assignee: updatedData.assignee ?? s.assignee,
          reporter: updatedData.reporter ?? s.reporter,
          jiraProjectId: updatedData.jiraProjectId ?? s.jiraProjectId,
        };
      }),
    );
    setEditingStory(null);
  }, []);

  const handleTcReadyChange = useCallback((ready) => {
    canGenerateTCsRef.current = ready;
  }, []);

  const handleGenerateTCs = useCallback(async () => {
    if (!createdJiraStories.length) return;
    setTcGenerateError(null);
    goTo(SCREENS.GENERATING_TCS);

    uploadedFileRef.current = uploadedFile ?? null;
    confluenceLinkRef.current = confluenceUrl || null;

    try {
      const response = await generateAITestCasesFromStories({
        stories: createdJiraStories.map((story) => ({
          key: story.key,
          summary: story.title || story.summary || '',
          description: story.description || story.summary || story.title || '',
          issuestatus: story.issuestatus || '',
          priority: story.priority || '',
          id: story.id,
        })),
        projectId,
        quantity: tcQuantity,
        docSummary,
      });

      const rawTcs = response?.test_cases || [];

      let globalIdx = 0;
      const allTcs = [];
      const newStoryGroups = [];

      const tcsByStoryKey = {};
      rawTcs.forEach((tc) => {
        if (!tcsByStoryKey[tc.story_key]) {
          tcsByStoryKey[tc.story_key] = [];
        }
        tcsByStoryKey[tc.story_key].push(tc);
      });

      Object.keys(tcsByStoryKey).forEach((storyKey) => {
        const story = createdJiraStories.find((s) => s.key === storyKey);
        if (!story) return;

        const storyMeta = {
          id: story.id,
          key: story.key,
          title: story.title || story.summary || '',
        };
        const tcs = tcsByStoryKey[storyKey];
        const tcInternalIds = [];

        tcs.forEach((tc) => {
          const mapped = mapApiTcToInternal(tc, globalIdx, selectedTestSuites, storyMeta);
          allTcs.push(mapped);
          tcInternalIds.push(globalIdx);
          globalIdx += 1;
        });

        if (tcInternalIds.length > 0) {
          newStoryGroups.push({ story, tcInternalIds });
        }
      });

      if (!allTcs.length) {
        setTcGenerateError(`No test cases were generated because the provided input doesn't contain enough information. Please add more details and try again.`);
        goTo(SCREENS.GENERATE_TCS);
        return;
      }

      setReviewTCs(allTcs);
      setStoryGroups(newStoryGroups);
      setTokenUsageTCs(response?.token_usage || null);
      setExistingMatches(response?.existing_matches || []);
      setSelectedExisting(new Set());
      goTo(SCREENS.REVIEW_TCS);
    } catch (err) {
      setTcGenerateError(err?.message ?? 'Failed to generate test cases. Please try again.');
      goTo(SCREENS.GENERATE_TCS);
    }
  }, [goTo, projectId, createdJiraStories, selectedTestSuites, tcQuantity, uploadedFile, confluenceUrl]);

  const handleEditTc = useCallback((tc) => {
    const initial = {
      ...defaultFormData,
      testsuites: selectedTestSuites,
      testSteps: tc.testSteps || [],
      ...tc,
    };
    tcEditClosingRef.current = false;
    originalTcRef.current = initial;
    setEditingTc(initial);
    setCreateStep(1);
  }, [selectedTestSuites]);

  const handleCloseTcEdit = useCallback(() => {
    const isDirty = editingTc && JSON.stringify(editingTc) !== JSON.stringify(originalTcRef.current);
    if (isDirty) {
      setShowTcDiscardConfirm(true);
    } else {
      tcEditClosingRef.current = true;
      setShowStepsModal(false);
      setEditingTc(null);
      setCreateStep(1);
      originalTcRef.current = null;
    }
  }, [editingTc]);

  const handleDiscardTcEdit = useCallback(() => {
    tcEditClosingRef.current = true;
    setShowTcDiscardConfirm(false);
    setShowStepsModal(false);
    setEditingTc(null);
    setCreateStep(1);
    originalTcRef.current = null;
  }, []);

  const handleSaveTcEdit = useCallback(() => {
    if (!editingTc) return;

    setReviewTCs((prev) =>
      prev.map((t) =>
        t._id === editingTc._id
          ? { ...t, ...editingTc }
          : t
      )
    );

    tcEditClosingRef.current = true;
    setShowStepsModal(false);
    setShowTcDiscardConfirm(false);
    setEditingTc(null);
    setCreateStep(1);
    originalTcRef.current = null;
  }, [editingTc]);

  const isGenerating =
    screen === SCREENS.GENERATING_STORIES ||
    screen === SCREENS.CREATING_JIRA ||
    screen === SCREENS.GENERATING_TCS;

  const isBulkActive =
    savePhase === SAVE_PHASE.SAVING || savePhase === SAVE_PHASE.LINKING;

  const showStepper = !isGenerating && screen !== SCREENS.JIRA_SUCCESS && savePhase !== SAVE_PHASE.SUCCESS;

  const getActions = () => {
    if (isGenerating || isBulkActive || savePhase === SAVE_PHASE.SUCCESS || screen === SCREENS.JIRA_SUCCESS) return null;

    if (screen === SCREENS.UPLOAD) {
      return {
        secondary: { label: 'Cancel', onClick: onClose },
        primary: {
          label: 'Generate stories',
          icon: 'forward',
          onClick: handleGenerateStories,
          disabled: !canGenerateStories,
        },
      };
    }

    if (screen === SCREENS.REVIEW_STORIES) {
      const selectedStories = stories.filter((s) => s.selected);
      const hasUnassignedProjects =
        linkedJiraProjects.length > 0 &&
        selectedStories.some((s) => !s.jiraProjectId);
      return {
        secondary: { label: 'Back', icon: 'back', onClick: () => goTo(SCREENS.UPLOAD) },
        primary: {
          label: 'Create in Jira',
          icon: 'forward',
          onClick: handleCreateInJira,
          disabled: selectedStories.length === 0 || hasUnassignedProjects,
        },
      };
    }

    if (screen === SCREENS.GENERATE_TCS) {
      return {
        secondary: { label: 'Back', icon: 'back', onClick: () => goTo(SCREENS.JIRA_SUCCESS) },
        primary: {
          label: 'Generate',
          icon: 'generate',
          onClick: handleGenerateTCs,
          disabled: !canGenerateTCsRef.current,
        },
      };
    }

    if (screen === SCREENS.REVIEW_TCS) {
      if (savePhase === SAVE_PHASE.PARTIAL || savePhase === SAVE_PHASE.ERROR) {
        return {
          secondary: {
            label: 'Retry Failed',
            onClick: () => { setSavePhase(SAVE_PHASE.IDLE); setSaveResult(null); },
          },
          primary: {
            label: 'Close',
            onClick: () => { refetchData?.(); navigateToTestCases(); },
          },
        };
      }
      return {
        secondary: { label: 'Back', icon: 'back', onClick: () => goTo(SCREENS.GENERATE_TCS) },
        primary: {
          label: 'Save test cases',
          onClick: handleBulkSave,
          disabled: selectedTCs.length === 0 || hasUnresolvedWarnings,
        },
      };
    }

    return null;
  };

  const renderContent = () => {
    if (screen === SCREENS.GENERATING_STORIES) {
      return (
        <WizardGeneratingOverlay
          title="Generating Jira Stories…"
          subtitle="AI is analysing your document and extracting requirements"
        />
      );
    }
    if (screen === SCREENS.CREATING_JIRA) {
      return (
        <WizardGeneratingOverlay
          title="Creating stories in Jira…"
          subtitle="Creating selected stories and attaching the document in Jira. This may take a moment."
        />
      );
    }
    if (screen === SCREENS.GENERATING_TCS) {
      return (
        <WizardGeneratingOverlay
          title="Generating test cases…"
          subtitle="AI is creating test cases from your Jira stories"
        />
      );
    }

    if (screen === SCREENS.UPLOAD) {
      return (
        <>
          {generateError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setGenerateError(null)}>
              {generateError}
            </Alert>
          )}
          <UploadScreen
            uploadedFile={uploadedFile} onFileChange={setUploadedFile}
            confluenceUrl={confluenceUrl} onUrlChange={setConfluenceUrl}
            inputTab={inputTab} onTabChange={setInputTab}
          />
        </>
      );
    }

    if (screen === SCREENS.REVIEW_STORIES) {
      return (
        <ReviewStoriesScreen
          stories={stories}
          onStoriesChange={setStories}
          tokenUsage={tokenUsageStories}
          onEditStory={handleEditStory}
          linkedJiraProjects={linkedJiraProjects}
          hasReporterWarning={hasReporterWarning}
        />
      );
    }

    if (screen === SCREENS.JIRA_SUCCESS) {
      return (
        <WizardSuccessScreen
          variant="story"
          createdItems={createdJiraStories}
          subtitle="Document attached to each story in Jira"
          onChoice={handleJiraChoice}
          continueLabel="Continue - generate test cases now"
          continueDesc="Create test cases from these stories immediately"
          exitLabel="Exit - I'll create test cases later"
          exitDesc="Use these stories as a starting point and create test cases when you're ready"
        />
      );
    }

    if (screen === SCREENS.GENERATE_TCS) {
      return (
        <>
          {tcGenerateError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setTcGenerateError(null)}>
              {tcGenerateError}
            </Alert>
          )}
          <GenerateTCsScreen
            createdJiraStories={createdJiraStories}
            uploadedFile={uploadedFile}
            confluenceUrl={confluenceUrl}
            inputTab={inputTab}
            quantity={tcQuantity}
            onQuantityChange={setTcQuantity}
            selectedTestSuites={selectedTestSuites}
            onTestSuitesChange={setSelectedTestSuites}
            testSuites={testSuites}
            projectId={projectId}
            onReadyChange={handleTcReadyChange}
          />
        </>
      );
    }

    if (screen === SCREENS.REVIEW_TCS) {
      return (
        <ReviewTCsScreen
          tcs={reviewTCs}
          onTcsChange={setReviewTCs}
          tokenUsage={tokenUsageTCs}
          onEdit={handleEditTc}
          techLayerOptions={technologyLayers}
          modules={modules}
          labels={labels}
          savePhase={savePhase}
          saveProgress={saveProgress}
          saveResult={saveResult}
          existingMatches={existingMatches}
          selectedExisting={[...selectedExisting]}
          onExistingToggle={handleExistingToggle}
          selectedStory={null}
          showStoryKey
          showMatching={existingMatches.length > 0}
        />
      );
    }

    return null;
  };

  return (
    <>
      <WizardShell
        open={open && !modalHidden}
        onClose={onClose}
        title={MODAL_TITLE[screen]}
        width="md"
        steps={WIZARD_STEPS}
        activeStep={STEPPER_INDEX[screen]}
        showStepper={showStepper}
        isGenerating={isGenerating}
        actions={getActions()}
      >
        {renderContent()}
      </WizardShell>

      {editingStory && (
        <CreateJiraIssueForm
          show={!!editingStory}
          handleClose={() => setEditingStory(null)}
          editOnly
          currentUserJiraAccountId={currentUser?.jira_account_id ?? null}
          prefillData={editingStory}
          onSaveEdits={handleSaveEdits}
          linkedJiraProjects={linkedJiraProjects}
          currentProject={currentProject}
          projectId={projectId}
          fetchLinkedIssues={() => { }}
          entityId={null}
          entityType={null}
          entityKey={null}
        />
      )}

      {editingTc && (
        <Modal
          open={!!editingTc}
          width="md"
          handleClose={handleCloseTcEdit}
          modalTitle="Edit Test Case"
          action={() => (
            <>
              <Button variant="outlined" color="secondary" onClick={handleCloseTcEdit}>
                Cancel
              </Button>
              {createStep > 1 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => validateAllAndSubmitRef._goToPrevStep?.()}
                >
                  Back
                </Button>
              )}
              {createStep < 3 ? (
                <Button
                  variant="contained"
                  onClick={() => validateAllAndSubmitRef._goToNextStep?.()}
                >
                  Next
                </Button>
              ) : (
                <Button variant="contained" onClick={() => validateAllAndSubmitRef.current?.(handleSaveTcEdit)}>
                  Save Changes
                </Button>
              )}
            </>
          )}
        >
          <CreateTestCaseForm
            formData={editingTc}
            setFormData={setEditingTcGuarded}
            isEditing={true}
            defaultFormData={defaultFormData}
            statuses={statuses}
            testSuites={testSuites}
            priorities={priorities}
            modules={modules}
            labels={labels}
            projectUsers={projectUsers}
            loading={false}
            technologyLayers={technologyLayers}
            isCreateMode={true}
            onAddSteps={() => setShowStepsModal(true)}
            stepsCount={editingTc?.testSteps?.length || 0}
            createStep={createStep}
            setCreateStep={setCreateStep}
            validateAllAndSubmitRef={validateAllAndSubmitRef}
          />
        </Modal>
      )}

      <ConfirmationModal
        show={showTcDiscardConfirm}
        handleClose={() => setShowTcDiscardConfirm(false)}
        handleConfirm={handleDiscardTcEdit}
        title="Discard Changes"
        body="Are you sure you want to cancel? Your edits to this test case will be lost."
        buttonLabel="Discard"
        buttonLabelSecondary="Keep Editing"
        buttonStyle="primary"
      />

      {showStepsModal && (
        <InMemoryStepsModal
          show={showStepsModal}
          handleClose={() => setShowStepsModal(false)}
          steps={editingTc?.testSteps || []}
          onSave={(newSteps) => setEditingTc(prev => ({ ...prev, testSteps: newSteps }))}
          technologyLayer={
            technologyLayers?.find((t) => t.id === editingTc?.technologyLayer)?.name
          }
        />
      )}
      <AiFeedbackModal
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        projectId={projectId}
        agentType="TEST_CASE_GENERATOR"
        entityType="TEST_CASE"
        entityIds={feedbackTcIds}
      />
    </>
  );
};

export default DocumentToJiraWizard;